// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import {
	PREMIUM_PRODUCT_FULL_NAME,
	PRODUCT_NAME,
	VIP_PRODUCT_NAME,
} from '@app/features/app/config/I18nDisplayConstants';
import {
	BugHunterBadgeIcon,
	FoundingMemberBadgeIcon,
	PartnerBadgeIcon,
	PrimeBadgeIcon,
} from '@app/features/user/components/badges/LafonBadgeIcons';
import {AchievementBadgeArt} from '@app/features/user/achievements/AchievementBadgeArt';
import {useUserAchievements} from '@app/features/user/achievements/AchievementStats';
import {openBadgeCollection, type SpecialBadgeEntry} from '@app/features/user/achievements/BadgeCollectionModal';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import styles from '@app/features/user/components/popouts/UserProfileBadges.module.css';
import type {Profile} from '@app/features/user/models/Profile';
import type {User} from '@app/features/user/models/User';
import Users from '@app/features/user/state/Users';
import * as DateUtils from '@app/features/user/utils/DateFormatting';
import {formatVisionarySequenceLabel} from '@app/features/user/utils/VisionarySequence';
import {PublicUserFlags, UserPremiumTypes} from '@fluxer/constants/src/UserConstants';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';

const BADGE_ASSET_BASE_URL = 'https://api.lafon.com.tr/badges';
const BADGE_ASSET_VERSION = '9';

const badgeAssetUrl = (fileName: string) => `${BADGE_ASSET_BASE_URL}/${fileName}?v=${BADGE_ASSET_VERSION}`;

const STAFF_DESCRIPTOR = msg({
	message: '{productName} Staff',
	comment:
		'Short badge title in the user profile badges popout. Preserve {productName}; it is inserted by code. English locales use Title Case for official badge titles; other locales should use natural local capitalization.',
});
const PARTNER_DESCRIPTOR = msg({
	message: '{productName} Partner',
	comment:
		'Short badge title in the user profile badges popout. Preserve {productName}; it is inserted by code. English locales use Title Case for official badge titles; other locales should use natural local capitalization.',
});
const BUG_HUNTER_DESCRIPTOR = msg({
	message: '{productName} Bug Hunter',
	comment:
		'Short badge title in the user profile badges popout. Preserve {productName}; it is inserted by code. English locales use Title Case for official badge titles; other locales should use natural local capitalization.',
});
const VISIONARY_SINCE_DESCRIPTOR = msg({
	message: '{vipProductName} · {premiumSinceFormatted} tarihinden beri',
	comment:
		'Badge title with a date in the user profile badges popout. Preserve {vipProductName}, {premiumSinceFormatted}; they are inserted by code.',
});
const VISIONARY_DESCRIPTOR = msg({
	message: '{vipProductName}',
	comment: 'Short badge title for the lifetime VIP tier. Preserve {vipProductName}; it is inserted by code.',
});
const SUBSCRIBER_SINCE_DESCRIPTOR = msg({
	message: '{premiumProductFullName} · {premiumSinceFormatted} tarihinden beri',
	comment:
		'Badge label with a date in the user profile badges popout. Preserve {premiumProductFullName}, {premiumSinceFormatted}; they are inserted by code.',
});
const VISIONARY_ID_DESCRIPTOR = msg({
	message: 'Lafon ViP ID {visionaryIdLabel}',
	comment:
		'Short label in the user profile badges popout. Keep it concise. Preserve {visionaryIdLabel}; it is inserted by code.',
});
const FOUNDING_MEMBER_DESCRIPTOR = msg({
	message: '{sequenceLabel} - {productName} İlk 5000 Üyesi',
	comment:
		'Badge tooltip for founding members in the first 5000 accounts. Preserve {sequenceLabel} and {productName}; they are inserted by code. sequenceLabel is like #1.',
});

interface BaseBadge {
	key: string;
	tooltip: string;
	url?: string;
}

interface IconBadge extends BaseBadge {
	type: 'icon';
	iconUrl: string;
}

interface TextBadge extends BaseBadge {
	type: 'text';
	text: string;
}

interface ComponentBadge extends BaseBadge {
	type: 'component';
	renderIcon: (size: number, className: string) => React.ReactNode;
	tooltipMultiline?: boolean;
}

type Badge = IconBadge | TextBadge | ComponentBadge;

type BadgeGroup = 'all' | 'founding' | 'standard';

interface UserProfileBadgesProps {
	user: User;
	profile: Profile | null;
	isModal?: boolean;
	isMobile?: boolean;
	/** Inline next to username#tag. Overlay floats on the profile card. */
	placement?: 'inline' | 'overlay' | 'row';
	/** founding = first-5000 only; standard = all other badges; all = both */
	group?: BadgeGroup;
	/** When set, shows the guild boost badge after the founding-member badge. */
	guildBoostSince?: Date | null;
}

const FOUNDING_BADGE_KEY = 'founding_member';
const MAX_VISIBLE_BADGES = 6;
const SPECIAL_BADGE_DESCRIPTIONS: Record<string, MessageDescriptor> = {
	staff: msg({
		message: 'Given to the people who build and run {productName}.',
		comment: 'Badge modal description of the staff badge. Preserve {productName}.',
	}),
	partner: msg({
		message: 'Given to official {productName} partners.',
		comment: 'Badge modal description of the partner badge. Preserve {productName}.',
	}),
	bug_hunter: msg({
		message: 'Given to members who found and reported bugs that helped improve {productName}.',
		comment: 'Badge modal description of the bug hunter badge. Preserve {productName}.',
	}),
	premium: msg({
		message: 'Given to lifetime VIP members. It never expires.',
		comment: 'Badge modal description of the lifetime VIP badge.',
	}),
	premium_sequence: msg({
		message: 'The order in which this member joined lifetime VIP.',
		comment: 'Badge modal description of the VIP sequence number badge.',
	}),
	founding_member: msg({
		message: 'Given permanently to the first 5,000 members who joined {productName}.',
		comment: 'Badge modal description of the founding member badge. Preserve {productName}.',
	}),
};
const SPECIAL_BADGE_ACCENTS: Record<string, string> = {
	staff: '#5b8def',
	partner: '#2fb5d2',
	bug_hunter: '#2ecc8f',
	premium: '#f5c84c',
	premium_sequence: '#f5c84c',
	founding_member: '#ff9f1c',
};
const MORE_BADGES_DESCRIPTOR = msg({
	message: 'Show all badges',
	comment: 'Tooltip of the "+N" chip after the visible profile badges.',
});
const GUILD_BOOST_BADGE_KEY = 'guild_boost';


interface BadgeOwnerInfo {
	premiumType: number | null;
	premiumSince: Date | null;
	premiumLifetimeSequence: number | null;
	registrationSequence: number | null;
}

function ownerInfo(user: User, profile: Profile | null): BadgeOwnerInfo {
	return {
		premiumType: profile ? profile.premiumType : (user.premiumType ?? null),
		premiumSince: profile ? profile.premiumSince : (user.premiumSince ?? null),
		premiumLifetimeSequence: profile ? (profile.premiumLifetimeSequence ?? null) : (user.premiumLifetimeSequence ?? null),
		registrationSequence: profile?.registrationSequence ?? user.registrationSequence ?? null,
	};
}

/** Non-progression badges (staff, partner, VIP, founding member…) shown in the profile row. */
function buildProfileBadges(user: User, info: BadgeOwnerInfo, i18n: I18n): Array<Badge> {
	const result: Array<Badge> = [];
	if (user.flags & PublicUserFlags.STAFF) {
		result.push({
			type: 'icon',
			key: 'staff',
			iconUrl: badgeAssetUrl('lafon-staff.svg'),
			tooltip: i18n._(STAFF_DESCRIPTOR, {productName: PRODUCT_NAME}),
			url: Routes.careers(),
		});
	}
	if (user.flags & PublicUserFlags.PARTNER) {
		result.push({
			type: 'component',
			key: 'partner',
			renderIcon: (size, className) => <PartnerBadgeIcon className={className} size={size} />,
			tooltip: i18n._(PARTNER_DESCRIPTOR, {productName: PRODUCT_NAME}),
			url: Routes.partners(),
		});
	}
	if (user.flags & PublicUserFlags.BUG_HUNTER) {
		result.push({
			type: 'component',
			key: 'bug_hunter',
			renderIcon: (size, className) => <BugHunterBadgeIcon className={className} size={size} />,
			tooltip: i18n._(BUG_HUNTER_DESCRIPTOR, {productName: PRODUCT_NAME}),
			url: Routes.bugs(),
		});
	}
	if (info.premiumType && info.premiumType !== UserPremiumTypes.NONE) {
		let tooltipText = PREMIUM_PRODUCT_FULL_NAME;
		let badgeUrl = Routes.plutonium();
		if (info.premiumType === UserPremiumTypes.LIFETIME) {
			if (info.premiumSince) {
				const premiumSinceFormatted = DateUtils.getFormattedShortDate(info.premiumSince);
				tooltipText = i18n._(VISIONARY_SINCE_DESCRIPTOR, {
					vipProductName: VIP_PRODUCT_NAME,
					premiumSinceFormatted,
				});
			} else {
				tooltipText = i18n._(VISIONARY_DESCRIPTOR, {vipProductName: VIP_PRODUCT_NAME});
			}
			badgeUrl = Routes.helpArticle('visionary');
		} else if (info.premiumSince) {
			const premiumSinceFormatted = DateUtils.getFormattedShortDate(info.premiumSince);
			tooltipText = i18n._(SUBSCRIBER_SINCE_DESCRIPTOR, {
				premiumProductFullName: PREMIUM_PRODUCT_FULL_NAME,
				premiumSinceFormatted,
			});
		}
		// Monthly Pro is shown by the "Pro Journey" progression badge; only lifetime VIP keeps its own.
		if (info.premiumType === UserPremiumTypes.LIFETIME) {
			result.push({
				type: 'component',
				key: 'premium',
				renderIcon: (size, className) => <PrimeBadgeIcon className={className} size={size} />,
				tooltip: tooltipText,
				url: badgeUrl,
			});
		}
		if (info.premiumType === UserPremiumTypes.LIFETIME && info.premiumLifetimeSequence != null) {
			const visionaryIdLabel = formatVisionarySequenceLabel(info.premiumLifetimeSequence);
			result.push({
				type: 'text',
				key: 'premium_sequence',
				text: visionaryIdLabel,
				tooltip: i18n._(VISIONARY_ID_DESCRIPTOR, {visionaryIdLabel}),
				url: badgeUrl,
			});
		}
	}
	const foundingSequence = info.registrationSequence;
	if (foundingSequence != null && foundingSequence >= 1 && foundingSequence <= 5000) {
		result.push({
			type: 'component',
			key: 'founding_member',
			renderIcon: (size, className) => <FoundingMemberBadgeIcon className={className} size={size} />,
			tooltip: i18n._(FOUNDING_MEMBER_DESCRIPTOR, {
				sequenceLabel: `#${foundingSequence}`,
				productName: PRODUCT_NAME,
			}),
		});
	}
	return result;
}

/** The same badges as entries for the badge modal. */
export function buildSpecialBadgeEntries(user: User, profile: Profile | null, i18n: I18n): Array<SpecialBadgeEntry> {
	const result = buildProfileBadges(user, ownerInfo(user, profile), i18n);
// Server boosting is shown by the "Server Booster" progression badge.
	// The VIP ID number is a profile-row label only; the badge modal never lists it.
	return result
		.filter((badge) => badge.key !== 'premium_sequence')
		.map((badge) => ({
		key: badge.key,
		title: badge.tooltip,
		description: SPECIAL_BADGE_DESCRIPTIONS[badge.key]
			? i18n._(SPECIAL_BADGE_DESCRIPTIONS[badge.key], {productName: PRODUCT_NAME})
			: undefined,
		accent: SPECIAL_BADGE_ACCENTS[badge.key],
		renderIcon: (size: number) =>
			badge.type === 'icon' ? (
				<img src={badge.iconUrl} alt="" width={size} height={size} />
			) : badge.type === 'component' ? (
				badge.renderIcon(size, '')
			) : (
				<span className={styles.sequenceBadge} style={{fontSize: size * 0.4}}>
					{badge.text}
				</span>
			),
	}));
}

export const UserProfileBadges: React.FC<UserProfileBadgesProps> = observer(
	({user, profile, isModal = false, isMobile = false, placement, group = 'all'}) => {
		const {i18n} = useLingui();
		const achievements = useUserAchievements(user, profile);
		const {badges, specialBadges} = useMemo(() => {
			const result = buildProfileBadges(user, ownerInfo(user, profile), i18n);
			const special = buildSpecialBadgeEntries(user, profile, i18n);
			const earnedAchievements: Array<Badge> = achievements
				.filter((progress) => progress.tierIndex >= 0)
				.map((progress) => ({
					type: 'component',
					key: `achievement_${progress.category.id}`,
					tooltip: `${i18n._(progress.category.title)} · ${i18n._(progress.category.tiers[progress.tierIndex].name)}`,
					renderIcon: (size, className) => (
						<AchievementBadgeArt
							categoryId={progress.category.id}
							tierIndex={progress.tierIndex}
							tierCount={progress.category.tiers.length}
							size={size}
							className={className}
						/>
					),
				}));
			let visible = result;
			if (group === 'founding') {
				visible = result.filter((badge) => badge.key === FOUNDING_BADGE_KEY || badge.key === GUILD_BOOST_BADGE_KEY);
			} else if (group === 'standard') {
				visible = [
					...result.filter((badge) => badge.key !== FOUNDING_BADGE_KEY && badge.key !== GUILD_BOOST_BADGE_KEY),
					...earnedAchievements,
				];
			} else {
				visible = [...result, ...earnedAchievements];
			}
			return {badges: visible, specialBadges: special};
		}, [
			achievements,
			group,
			user.flags,
			user.registrationSequence,
			profile?.registrationSequence,
			profile?.premiumType,
			profile?.premiumSince,
			profile?.premiumLifetimeSequence,
			i18n.locale,
		]);
		if (badges.length === 0) {
			return null;
		}
		const resolvedPlacement = placement ?? (isModal ? 'inline' : 'overlay');
		const containerClassName =
			resolvedPlacement === 'row'
				? clsx(styles.containerRow, isMobile && styles.containerRowMobile)
				: resolvedPlacement === 'inline'
				? clsx(
						styles.containerInline,
						isMobile ? styles.containerInlineMobile : styles.containerInlineDesktop,
					)
				: styles.containerOverlay;
		const badgeClassName = isMobile ? styles.badgeMobile : styles.badgeDesktop;
		const badgeSize = isMobile ? 28 : 20;
		const openCollection = (initialKey?: string) =>
			openBadgeCollection({
				user,
				profile,
				specialBadges,
				getOwnSpecialBadges: () =>
					Users.currentUser ? buildSpecialBadgeEntries(Users.currentUser, null, i18n) : [],
				initialKey: initialKey?.replace(/^achievement_/, ''),
			});
		const renderInteractiveWrapper = (key: string, children: React.ReactNode) => (
			<button
				type="button"
				className={clsx(styles.link, styles.badgeButton)}
				onClick={(event) => {
					event.stopPropagation();
					openCollection(key);
				}}
				data-flx="user.user-profile-badges.badge-button"
			>
				{children}
			</button>
		);
		const shownBadges = badges.length > MAX_VISIBLE_BADGES ? badges.slice(0, MAX_VISIBLE_BADGES) : badges;
		const hiddenCount = badges.length - shownBadges.length;
		return (
			<div className={containerClassName} data-flx="user.user-profile-badges.div">
				{shownBadges.map((badge) => {
					const sequenceClassName = isMobile ? styles.sequenceBadgeMobile : styles.sequenceBadgeDesktop;
					const badgeContent =
						badge.type === 'icon' ? (
							<img
								src={badge.iconUrl}
								alt={badge.tooltip}
								className={badgeClassName}
								data-flx="user.user-profile-badges.img"
							/>
						) : badge.type === 'component' ? (
							<span className={clsx(styles.componentBadge, badgeClassName)} data-flx="user.user-profile-badges.component">
								{badge.renderIcon(badgeSize, styles.componentBadgeIcon)}
							</span>
						) : (
							<span
								className={clsx(styles.sequenceBadge, sequenceClassName)}
								aria-hidden="true"
								data-flx="user.user-profile-badges.sequence-badge"
							>
								{badge.text}
							</span>
						);
					const tooltipText =
						badge.type === 'component' && badge.tooltipMultiline
							? () => <span className={styles.multilineTooltip}>{badge.tooltip}</span>
							: badge.tooltip;
					return (
						<Tooltip key={badge.key} text={tooltipText} maxWidth="xl" data-flx="user.user-profile-badges.tooltip">
							<FocusRing offset={-2} data-flx="user.user-profile-badges.focus-ring">
								{renderInteractiveWrapper(badge.key, badgeContent)}
							</FocusRing>
						</Tooltip>
					);
				})}
				{hiddenCount > 0 ? (
					<Tooltip text={i18n._(MORE_BADGES_DESCRIPTOR)} data-flx="user.user-profile-badges.more-tooltip">
						<FocusRing offset={-2} data-flx="user.user-profile-badges.more-focus-ring">
							<button
								type="button"
								className={clsx(styles.moreChip, isMobile && styles.moreChipMobile)}
								onClick={(event) => {
									event.stopPropagation();
									openCollection();
								}}
								data-flx="user.user-profile-badges.more"
							>
								+{hiddenCount}
							</button>
						</FocusRing>
					</Tooltip>
				) : null}
			</div>
		);
	},
);
